import React, { useState } from 'react';
import ModalWrapper from './ModalWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

export default function AddStockModal({ open, onClose, onSuccess, customers, getClientOptions }) {
  const [form, setForm] = useState({
    productCode: '',
    barcode: '',
    labelSize: '',
    minQty: '',
    currentQty: '',
    clientId: '',
    description: '',
    customDescription: '',
    variant: '',
    customVariant: '',
    cost: '',
    notes: ''
  });
  const [descOptions, setDescOptions] = useState([]);
  const [variantOptions, setVariantOptions] = useState([]);

  // Track previous clientId to only reset fields if customer actually changes
  const prevClientIdRef = React.useRef(form.clientId);
  
  // Update descriptions when customer changes
  React.useEffect(() => {
    if (form.clientId) {
      const descs = getClientOptions ? getClientOptions('descriptions', form.clientId) : [];
      setDescOptions(descs);
      if (prevClientIdRef.current !== form.clientId) {
        setForm(f => ({ ...f, description: '', customDescription: '', variant: '', customVariant: '' }));
        prevClientIdRef.current = form.clientId;
      }
    } else {
      setDescOptions([]);
      setVariantOptions([]);
      setForm(f => ({ ...f, description: '', customDescription: '', variant: '', customVariant: '' }));
      prevClientIdRef.current = '';
    }
  }, [form.clientId, getClientOptions]);
  
  // Update variants when customer or description changes
  React.useEffect(() => {
    if (form.clientId && form.description && form.description !== 'new') {
      const vars = getClientOptions ? getClientOptions('variants', form.clientId, form.description) : [];
      setVariantOptions(vars);
      // Reset variant selection when description changes
      setForm(f => ({ ...f, variant: '', customVariant: '' }));
    } else {
      setVariantOptions([]);
      setForm(f => ({ ...f, variant: '', customVariant: '' }));
    }
  }, [form.clientId, form.description, getClientOptions]);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };
  const handleDescriptionChange = e => {
    setForm(f => ({ ...f, description: e.target.value, customDescription: '' }));
  };
  const handleCustomDescription = e => {
    setForm(f => ({ ...f, customDescription: e.target.value }));
  };
  const handleVariantChange = e => {
    setForm(f => ({ ...f, variant: e.target.value, customVariant: '' }));
  };
  const handleCustomVariant = e => {
    setForm(f => ({ ...f, customVariant: e.target.value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    // Validation
    const descValue = form.description === 'new' ? form.customDescription : form.description;
    const variantValue = form.variant === 'new' ? form.customVariant : form.variant;
    if (!descValue || !form.productCode || !variantValue || !form.labelSize || !form.minQty || !form.currentQty || !form.clientId || !form.cost) {
      setFormError('Please fill in all required fields, including cost.');
      setSubmitting(false);
      return;
    }
    if (isNaN(form.cost) || Number(form.cost) <= 0) {
      setFormError('Cost must be a positive number (R/1000).');
      setSubmitting(false);
      return;
    }
    try {
      const payload = {
        productCode: form.productCode,
        barcode: form.barcode,
        labelSize: form.labelSize,
        minQty: Number(form.minQty),
        currentQty: Number(form.currentQty),
        clientId: form.clientId,
        mainCategory: descValue,
        variantDesc: variantValue,
        cost: Number(form.cost),
        notes: form.notes
      };
      const res = await fetch(`${API_URL}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        // Handle specific error cases
        if (res.status === 409) {
          // Duplicate field error (product code or barcode)
          const errorData = await res.json().catch(() => ({}));
          if (errorData.error) {
            if (errorData.error.includes('productCode')) {
              setFormError('Item number already allocated to another item');
            } else if (errorData.error.includes('barcode')) {
              setFormError('Barcode already allocated to another item');
            } else {
              // Generic duplicate error - could be either field
              setFormError('Item number or barcode already allocated to another item');
            }
          } else {
            setFormError('Item number or barcode already allocated to another item');
          }
        } else {
          // Other errors
          const errorData = await res.json().catch(() => ({}));
          setFormError(errorData.error || 'Failed to add item');
        }
        setSubmitting(false);
        return;
      }
      
      const newItem = await res.json();
      onSuccess(newItem);
    } catch (err) {
      setFormError('Network error: ' + err.message);
    }
    setSubmitting(false);
  };

  return (
    <ModalWrapper open={open} onClose={onClose} zIndex={40} minWidth={680} maxWidth={780}>
      <form onSubmit={handleSubmit} style={{
        background: 'linear-gradient(135deg, #f8fafc 70%, #e3f2fd 100%)',
        padding: 0,
        borderRadius: 0,
        minWidth: 'unset',
        maxWidth: 'unset',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 0
      }}>
        <h2 style={{marginBottom: 8, fontWeight: 700, color: '#1976d2', letterSpacing: 1}}>Add Stock Item</h2>

        <div style={{display:'flex', flexDirection:'column', gap:14}}>
          <div style={{display:'flex', gap:16}}>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Customer</label>
              <select name="clientId" value={form.clientId} onChange={handleChange} required style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Product Code</label>
              <input name="productCode" value={form.productCode} onChange={handleChange} placeholder="Product Code" required style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} />
            </div>
          </div>
          <div style={{display:'flex', gap:16}}>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Description</label>
              <select name="description" value={form.description} onChange={handleDescriptionChange} required style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} disabled={!form.clientId}>
                <option value="">{form.clientId ? 'Select Description' : 'Select Customer First'}</option>
                <option value="new">New...</option>
                {descOptions.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              {form.description === 'new' && (
                <input name="customDescription" value={form.customDescription} onChange={handleCustomDescription} placeholder="Enter new description" required style={{padding:10, borderRadius:6, border:'2px solid #1976d2'}} />
              )}
            </div>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Variant</label>
              <select name="variant" value={form.variant} onChange={handleVariantChange} required style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} disabled={!form.clientId}>
                <option value="">{form.clientId ? 'Select Variant' : 'Select Customer First'}</option>
                <option value="new">New...</option>
                {form.clientId && form.description && form.description !== 'new' && variantOptions
                  .filter(v => {
                    // If variants are stored as 'DescriptionName:VariantName' strings, filter by prefix
                    if (typeof v === 'string' && v.includes(':')) {
                      const [desc, variant] = v.split(':');
                      return desc === form.description;
                    }
                    // If variants are stored as objects with a description field
                    if (typeof v === 'object' && v !== null && v.description) {
                      return v.description === form.description;
                    }
                    // If variants are just strings and no structure, show all (legacy fallback)
                    return typeof v === 'string';
                  })
                  .map(v => {
                    // If string with ':', show only the variant part
                    if (typeof v === 'string' && v.includes(':')) {
                      const [, variant] = v.split(':');
                      return <option key={v} value={variant}>{variant}</option>;
                    }
                    // If object, show value property or fallback to string
                    return <option key={typeof v === 'object' ? v.value : v} value={typeof v === 'object' ? v.value : v}>
                      {typeof v === 'object' ? v.value : v}
                    </option>;
                  })}
              </select>
              {form.variant === 'new' && (
                <input name="customVariant" value={form.customVariant} onChange={handleCustomVariant} placeholder="Enter new variant" required style={{padding:10, borderRadius:6, border:'2px solid #1976d2'}} />
              )}
            </div>
          </div>
          <div style={{display:'flex', gap:16}}>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Barcode</label>
              <input name="barcode" value={form.barcode} onChange={handleChange} placeholder="Barcode" style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} />
            </div>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Notes</label>
              <input name="notes" value={form.notes} onChange={handleChange} placeholder="Optional notes about this item" style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} />
            </div>
          </div>
          <div style={{display:'flex', gap:16}}>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Label Size</label>
              <input name="labelSize" value={form.labelSize} onChange={handleChange} placeholder="Label Size" required style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} />
            </div>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Cost (R/1000)</label>
              <input name="cost" value={form.cost} onChange={handleChange} placeholder="Cost per 1000" type="number" required style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} />
            </div>
          </div>

          <div style={{display:'flex', gap:16}}>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Min Qty</label>
              <input name="minQty" value={form.minQty} onChange={handleChange} placeholder="Min Qty" type="number" required style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} />
            </div>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Current Qty</label>
              <input name="currentQty" value={form.currentQty} onChange={handleChange} placeholder="Current Qty" type="number" required style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} />
            </div>
            <div style={{flex:1, display:'flex', flexDirection:'column', gap:6}}>
              <label style={{fontWeight:500,marginBottom:2}}>Cost (R/1000) <span style={{color:'red'}}>*</span></label>
              <input name="cost" value={form.cost} onChange={handleChange} placeholder="e.g. 1200" type="number" min="0" step="0.01" required style={{padding:10, borderRadius:6, border:'1px solid #bdbdbd'}} />
            </div>
          </div>
        </div>
        {formError && <div style={{color:'#b71c1c',marginTop:8,fontWeight:500}}>{formError}</div>}
        <div style={{display:'flex',gap:16,justifyContent:'center',marginTop:18}}>
          <button type="submit" disabled={submitting} style={{background:'#1976d2',color:'white',fontWeight:600,padding:'10px 32px',border:'none',borderRadius:8,boxShadow:'0 2px 8px #1976d233',fontSize:16}}>Save</button>
          <button type="button" onClick={onClose} style={{background:'#fff',color:'#1976d2',fontWeight:600,padding:'10px 32px',border:'1.5px solid #1976d2',borderRadius:8,fontSize:16}}>Cancel</button>
        </div>
      </form>
    </ModalWrapper>
  );
}
